import React, { FC, useState } from 'react';
import { TextButton } from '../../../../widgets/buttons';
import { Segmented } from '../../../../widgets/segmented';
import {
    BehaviourArIcon,
    BehaviourVtoIcon,
    ChevronIcon,
    DockRightIcon,
    DockSideIcon,
    RailsCompactIcon,
    RailsExpandedIcon,
    SettingsBehavioursIcon,
    SettingsComposerIcon,
    SettingsInterfaceIcon,
    SettingsRenderingIcon,
    ThemeDarkIcon,
    ThemeLightIcon,
    ThemeSystemIcon
} from '../../../../widgets/icons/scene';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import AREditor from './scene-editors/arEditor';
import SettingsEditor from './scene-editors/settingsEditor';
import VTOEditor from './scene-editors/vtoEditor';
import { WorkspacePreferences } from '../configurator-scene-editor/useWorkspacePreferences';

// Un chevron solo, girato: nel prototipo la freccia che porta avanti e quella che
// torna indietro sono lo stesso segno a due orientamenti, non due disegni.
const ChevronRightIcon = () => <ChevronIcon width={16} height={16} style={{ transform: 'rotate(270deg)', flexShrink: 0 }} />;
const ChevronLeftIcon = () => <ChevronIcon width={16} height={16} style={{ transform: 'rotate(90deg)', flexShrink: 0 }} />;

type SettingsCategory = 'interface' | 'rendering' | 'composer' | 'behaviours';
type BehaviourPage = 'vto' | 'ar';

interface SceneEditorSettingsDialogProps {
    preferences: WorkspacePreferences;
    setPreference: <K extends keyof WorkspacePreferences>(key: K, value: WorkspacePreferences[K]) => void;
    sceneEditor: any;
    zkScene: any;
    zkProduct: any;
    additionalFeatures?: any;
    /**
     * QUI DENTRO SI TOCCA LA SCENA, e la scena va segnata da salvare.
     *
     * Chi tiene il pennarello ascolta gli eventi dentro l'area di lavoro, ma questa finestra si
     * disegna in un portale attaccato alla radice della pagina: quello che si cambia qui non
     * passa mai di lì. Senza questa richiamata, spegnere l'AR cambiava davvero la
     * configurazione e poi «Save» rispondeva «nulla da salvare» — il valore restava vivo fino
     * al ricaricamento e non arrivava mai al server.
     */
    onSceneChanged?: () => void;
}

// Non disegna: ascolta. `display: contents` lo toglie dal disegno, così le sezioni restano
// figlie di `Content` come prima e nessun layout si accorge che è passato di qui.
const SceneChanges = styled.div`
    display: contents;
`;

const DialogLayout = styled.div`
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    min-height: 460px;
    margin: 0 -20px;
    overflow: hidden;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

// La lista dei gruppi e la lista dei canali di Behaviours sono LA STESSA lista: glifo,
// titolo con la sua riga di sotto, chevron. Nel prototipo è un componente solo usato due
// volte, e due copie sarebbero due spaziature da tenere allineate a mano.
const ItemList = styled.nav`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CategoryList = styled(ItemList)`
    padding: 16px;
    border-right: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.pageBackgroundColor};
`;

const ItemButton = styled.button<{ $active?: boolean }>`
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: 8px;
    text-align: left;
    color: ${(props) => (props.$active ? props.theme.app.textColor : props.theme.app.primary)};
    background: ${(props) => (props.$active ? props.theme.app.backgroundColor : 'transparent')};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(38, 50, 56, 0.08)' : 'none')};
    cursor: pointer;

    &:hover,
    &:focus-visible {
        color: ${(props) => props.theme.app.textColor};
        outline: none;
    }
`;

// I glifi del prototipo sono a contorno: niente wrapper Icon, che li riempirebbe.
const ItemGlyph = styled.span`
    display: inline-flex;

    svg {
        width: 18px;
        height: 18px;
        display: block;
    }
`;

const ItemText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const ItemTitle = styled.span`
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    font-weight: 600;
`;

const ItemSubtitle = styled.span`
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
`;

const Content = styled.div`
    min-width: 0;
    padding: 20px 24px;
    overflow-x: hidden;
    overflow-y: auto;
`;

// La testata di una sezione, non un titolo di pagina: dice di quale famiglia sono le
// righe che seguono, e nel prototipo è scritta in maiuscoletto sopra la sua linea.
const SectionTitle = styled.h3`
    margin: 0 0 4px;
    color: ${(props) => props.theme.app.primary};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const SettingsSection = styled.section`
    display: flex;
    flex-direction: column;
`;

// Etichetta e comando, niente di più: la riga di spiegazione sotto il nome non c'è nel
// prototipo, e su sei righe faceva della lista un documento da leggere.
const SettingRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    min-height: 46px;
    padding: 8px 0;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};

    &:last-child {
        border-bottom: 0;
    }
`;

const SettingLabel = styled.label`
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
`;

const BackButton = styled(TextButton)`
    margin-bottom: 12px;
`;

const CATEGORIES: { key: SettingsCategory; icon: React.ReactNode; title: string; subtitle: string }[] = [
    { key: 'interface', icon: <SettingsInterfaceIcon />, title: T._('Interface', 'Admin'), subtitle: T._('Theme, panel layout', 'Admin') },
    { key: 'rendering', icon: <SettingsRenderingIcon />, title: T._('Rendering', 'Admin'), subtitle: T._('Anti-aliasing, textures', 'Admin') },
    { key: 'composer', icon: <SettingsComposerIcon />, title: T._('Composer', 'Admin'), subtitle: T._('Automation', 'Admin') },
    { key: 'behaviours', icon: <SettingsBehavioursIcon />, title: T._('Behaviours', 'Admin'), subtitle: T._('Virtual Try-On, AR', 'Admin') }
];

const SceneEditorSettingsDialog: FC<SceneEditorSettingsDialogProps> = ({
    preferences,
    setPreference,
    sceneEditor,
    zkScene,
    zkProduct,
    additionalFeatures,
    onSceneChanged
}) => {
    const [category, setCategory] = useState<SettingsCategory>('interface');
    const [behaviourPage, setBehaviourPage] = useState<BehaviourPage | null>(null);
    // Il gestore dei dialoghi conserva il nodo così com'è stato creato: le props non
    // tornano più. Lo stato corrente dei segmented sta quindi qui, e ogni scelta viene
    // comunque applicata subito fuori — un tema lo si giudica vedendolo. È «Cancel», in
    // fondo alla finestra, a rimettere le cose com'erano davvero.
    const [values, setValues] = useState(preferences);

    const choose = <K extends keyof WorkspacePreferences>(key: K, value: WorkspacePreferences[K]) => {
        setValues((current) => ({ ...current, [key]: value }));
        setPreference(key, value);
    };

    const setCategoryAndReset = (nextCategory: SettingsCategory) => {
        setCategory(nextCategory);
        setBehaviourPage(null);
    };

    // Tre righe, nell'ordine del prototipo. L'unità di misura non è qui perché vive nella
    // barra di stato sotto il palco, dove la si legge; i pannelli chiusi non sono qui perché
    // ogni pannello ha già il suo comando di chiusura, e una preferenza in due posti è una
    // preferenza che si contraddice.
    const renderInterface = () => (
        <SettingsSection>
            <SectionTitle>{T._('Appearance', 'Admin')}</SectionTitle>
            <SettingRow>
                <SettingLabel>{T._('Theme', 'Admin')}</SettingLabel>
                <Segmented
                    aria-label={T._('Interface theme', 'Admin')}
                    value={values.colorScheme}
                    onChange={(value) => choose('colorScheme', value)}
                    options={[
                        { value: 'light', icon: <ThemeLightIcon />, label: T._('Light', 'Admin') },
                        { value: 'dark', icon: <ThemeDarkIcon />, label: T._('Dark', 'Admin') },
                        { value: 'system', icon: <ThemeSystemIcon />, label: T._('Same as your computer', 'Admin') }
                    ]}
                />
            </SettingRow>
            <SettingRow>
                <SettingLabel>{T._('Properties panel', 'Admin')}</SettingLabel>
                <Segmented
                    aria-label={T._('Properties panel position', 'Admin')}
                    value={values.propertiesPanel}
                    onChange={(value) => choose('propertiesPanel', value)}
                    options={[
                        { value: 'side', icon: <DockSideIcon />, label: T._('Properties beside the list', 'Admin') },
                        { value: 'right', icon: <DockRightIcon />, label: T._('Properties on the right · materials move under the model', 'Admin') }
                    ]}
                />
            </SettingRow>
            <SettingRow>
                <SettingLabel>{T._('Section rails', 'Admin')}</SettingLabel>
                <Segmented
                    aria-label={T._('Section rails', 'Admin')}
                    value={values.sectionRails}
                    onChange={(value) => choose('sectionRails', value)}
                    options={[
                        { value: 'compact', icon: <RailsCompactIcon />, label: T._('Compact rails · icons only', 'Admin') },
                        { value: 'expanded', icon: <RailsExpandedIcon />, label: T._('Expanded rails · icons with names', 'Admin') }
                    ]}
                />
            </SettingRow>
        </SettingsSection>
    );

    const renderBehaviours = () => {
        if (!behaviourPage) {
            return (
                <>
                    <SectionTitle>{T._('Channels', 'Admin')}</SectionTitle>
                    <ItemList aria-label={T._('Behaviour channels', 'Admin')}>
                        <ItemButton type='button' onClick={() => setBehaviourPage('vto')}>
                            <ItemGlyph>
                                <BehaviourVtoIcon />
                            </ItemGlyph>
                            <ItemText>
                                <ItemTitle>{T._('Virtual Try-On', 'Admin')}</ItemTitle>
                                <ItemSubtitle>{T._('Type, options, reference files', 'Admin')}</ItemSubtitle>
                            </ItemText>
                            <ChevronRightIcon />
                        </ItemButton>
                        <ItemButton type='button' onClick={() => setBehaviourPage('ar')}>
                            <ItemGlyph>
                                <BehaviourArIcon />
                            </ItemGlyph>
                            <ItemText>
                                <ItemTitle>{T._('Augmented Reality', 'Admin')}</ItemTitle>
                                <ItemSubtitle>{T._('Placement, real-world scale, conversion', 'Admin')}</ItemSubtitle>
                            </ItemText>
                            <ChevronRightIcon />
                        </ItemButton>
                    </ItemList>
                    {/* I punti informativi stanno qui finché i trigger point non hanno la loro
                        sezione: è il gruppo delle funzioni che una casa propria non ce l'hanno. */}
                    <SettingsEditor
                        section='behaviours'
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        isAIEnabled={zkProduct?.get?.('isAiConfigurationEnabled')}
                        setAIEnabled={(enabled) => zkProduct?.set?.('isAiConfigurationEnabled', enabled)}
                        additionalFeatures={additionalFeatures}
                    />
                </>
            );
        }

        return (
            <>
                <BackButton iconPosition='left' onClick={() => setBehaviourPage(null)}>
                    <ChevronLeftIcon />
                    {T._('Back', 'Admin')}
                </BackButton>
                {behaviourPage === 'vto' ? (
                    <VTOEditor productModelID={zkProduct?.get?.('modelID')} additionalFeatures={additionalFeatures} />
                ) : (
                    <AREditor sceneEditor={sceneEditor} zkScene={zkScene} onSceneChanged={onSceneChanged} />
                )}
            </>
        );
    };

    return (
        <DialogLayout>
            <CategoryList aria-label={T._('Settings groups', 'Admin')}>
                {CATEGORIES.map((item) => (
                    <ItemButton
                        key={item.key}
                        type='button'
                        $active={category === item.key}
                        aria-current={category === item.key ? 'page' : undefined}
                        onClick={() => setCategoryAndReset(item.key)}
                    >
                        <ItemGlyph>{item.icon}</ItemGlyph>
                        <ItemText>
                            <ItemTitle>{item.title}</ItemTitle>
                            <ItemSubtitle>{item.subtitle}</ItemSubtitle>
                        </ItemText>
                        <ChevronRightIcon />
                    </ItemButton>
                ))}
            </CategoryList>
            <Content>
                {/* L’INTERFACCIA STA FUORI DALL’ASCOLTO, e non per dimenticanza: tema e disposizione
                    dei pannelli sono preferenze di chi guarda, non dati della scena. Segnarle da
                    salvare accenderebbe «modifiche non salvate» per aver cambiato colore allo sfondo. */}
                {category === 'interface' && renderInterface()}
                <SceneChanges onChangeCapture={onSceneChanged} onInputCapture={onSceneChanged}>
                    {category === 'rendering' && (
                        <SettingsEditor
                            section='rendering'
                            sceneEditor={sceneEditor}
                            zkScene={zkScene}
                            isAIEnabled={zkProduct?.get?.('isAiConfigurationEnabled')}
                            setAIEnabled={(enabled) => zkProduct?.set?.('isAiConfigurationEnabled', enabled)}
                            additionalFeatures={additionalFeatures}
                        />
                    )}
                    {category === 'composer' && (
                        <SettingsEditor
                            section='composer'
                            sceneEditor={sceneEditor}
                            zkScene={zkScene}
                            isAIEnabled={zkProduct?.get?.('isAiConfigurationEnabled')}
                            setAIEnabled={(enabled) => zkProduct?.set?.('isAiConfigurationEnabled', enabled)}
                            additionalFeatures={additionalFeatures}
                        />
                    )}
                    {category === 'behaviours' && renderBehaviours()}
                </SceneChanges>
            </Content>
        </DialogLayout>
    );
};

export default SceneEditorSettingsDialog;
